import { Device, Gateway, Group } from '@iotv/datamodel';
import { AppValueTypes, isBoolean } from '@iotv/iotv-v3-types';
import Assembler from '../../data/Assembler';
import { ducktype, getValueLabel } from '../../data/TypeHelpers';
const debug = process.env.REACT_APP_DEBUG && false;
const attributableAttKeys = ['String', 'Number', 'Date', 'Boolean', 'Enum', 'object'];
const getAttributeType = (attDefType) => {
    switch (attDefType) {
        case 'String':
            return AppValueTypes.STRING;
        case 'Number':
            return AppValueTypes.NUMBER;
        case 'Boolean':
            return AppValueTypes.BOOLEAN;
        case 'Date':
            return AppValueTypes.DATE;
        case 'Enum':
            return AppValueTypes.ENUM;
        case 'object':
            return AppValueTypes.OBJECT;
        default:
            return undefined;
    }
};
export const getViewDefintionFromObject = (possibleBusinessObject) => {
    const definitions = {};
    const object = Assembler.getInstance(possibleBusinessObject);
    if (object) {
        const attDefs = object.getUserAttributes();
        const filteredAttDefs = Object.fromEntries(Object.entries(attDefs).filter(([key, attDef]) => attDef !== undefined && (attDef.hidden !== true || (attDef.hidden === true && attDef.hideFromList === false))));
        Object.assign(definitions, getViewDefinitionFromUserAttributes(filteredAttDefs));
    }
    // debug && console.log(`getViewDefintionFromObject ${ possibleBusinessObject.type } returning`, definitions)
    return definitions;
};
export const getViewDefintionFromMutantZombie = (mutantZombie, keys) => {
    const getViewKeyDefinition = ([key, value]) => {
        const viewKeyDefinition = {
            editable: true,
            key,
            type: ducktype(key, value),
            label: key,
        };
        return viewKeyDefinition;
    };
    const viewDefinition = {};
    Object.entries(mutantZombie).forEach(([k, v]) => {
        if (!keys || keys.includes(k)) {
            viewDefinition[k] = getViewKeyDefinition([k, v]);
        }
    });
    return viewDefinition;
};
export const getViewDefinitionFromUserAttributes = (attDefs) => {
    const definitions = {};
    Object.entries(attDefs)
        .filter(([attKey, attDef]) => attDef && attributableAttKeys.includes(attDef.type))
        .forEach(([attKey, attDef]) => {
        if (attDef) {
            const viewKeyDefinition = getViewKeyDefinitionFromUserAttribute(attKey, attDef);
            if (viewKeyDefinition)
                definitions[attKey] = viewKeyDefinition;
        }
    });
    return definitions;
};
const getViewKeyDefinitionFromUserAttribute = (attKey, attDef) => {
    let viewKeyDefinition = null;
    const { type, validation, needConfirmation, label, displayFn, valueFn, valueFormatter, enumKv, editable, precision, units, description, limits, required, default: defaultValue, dependsOn, hidden, hideFromList, } = attDef;
    const confirmation = needConfirmation ? needConfirmation : false;
    const attributeType = getAttributeType(type);
    const validationRx = validation?.regex ? new RegExp(validation.regex) : undefined;
    const pass = validation?.pass;
    const resolvedLabel = typeof label === 'string' ? label : getValueLabel(attKey);
    if (attributeType) {
        viewKeyDefinition = {
            key: attKey,
            label: resolvedLabel,
            type: attributeType,
            required,
            needConfirmation: confirmation,
            editable: editable ?? confirmation ?? true,
            stateFn: undefined,
            validationRx,
            pass,
            failsValidationText: validation?.invalidMessage,
            valueFormatter: valueFormatter,
            valueGetter: displayFn,
            valueSetter: valueFn,
            precision,
            limits,
            unit: units,
            defaultValue,
            description,
            hideFromList,
            hidden,
            dependsOn: dependsOn
                ? (item) => {
                    const partialUserAttributeKeyDef = dependsOn(item);
                    if (partialUserAttributeKeyDef) {
                        const { required: overridenRequired, editable: overridenEditable, hidden: overridenHidden, default: overridenDefaultValue, label: overridenLabel, type: overridenType, enumKv: overridenEnumKv, } = partialUserAttributeKeyDef;
                        const overridenResolvedLabel = overridenLabel ? (typeof overridenLabel === 'string' ? overridenLabel : overridenLabel(attKey)) : resolvedLabel;
                        const partialViewKeyDef = {
                            required: isBoolean(overridenRequired) ? overridenRequired : required,
                            editable: isBoolean(overridenEditable) ? overridenEditable : editable,
                            defaultValue: overridenDefaultValue ? overridenDefaultValue : defaultValue,
                            label: overridenResolvedLabel,
                            hidden: isBoolean(overridenHidden) ? overridenHidden : hidden,
                            type: overridenType ? getAttributeType(overridenType) : attributeType,
                            enumKV: overridenEnumKv ? overridenEnumKv : enumKv,
                        };
                        return partialViewKeyDef;
                    }
                    else {
                        return null;
                    }
                }
                : undefined,
        };
        if (attributeType === AppValueTypes.ENUM && enumKv) {
            viewKeyDefinition.enumKV = enumKv;
        }
        if (attributeType === AppValueTypes.OBJECT) {
            debug && console.log(viewKeyDefinition);
        }
        if (attributeType === AppValueTypes.DATE) {
            debug && console.log(`Date viewKeyDefinition`, viewKeyDefinition);
        }
    }
    return viewKeyDefinition;
};
export const commonListViewDefinition = {
    name: {
        key: 'name',
        label: 'Name',
        type: AppValueTypes.STRING,
        editable: true,
        unit: undefined,
        precision: undefined,
        stateFn: undefined,
        validationRx: undefined,
    },
    description: {
        key: 'description',
        label: 'Description',
        type: AppValueTypes.STRING,
        editable: true,
        unit: undefined,
        precision: undefined,
        stateFn: undefined,
        validationRx: undefined,
    },
};
export const genericListViewDefinition = {
    ...commonListViewDefinition,
    id: {
        key: 'id',
        label: 'ID',
        type: AppValueTypes.STRING,
        editable: false,
        unit: undefined,
        precision: undefined,
        stateFn: undefined,
        validationRx: undefined,
    },
};
const zombieGroup = new Group({ supportedType: undefined, type: 'Group' });
/**
 * @deprecated
 * CRA — 2025 09: We should be trying to construct the view definition / user attributes from a real class instance, so that `this` works properly
 */
export const basicGroupListViewDefinition = getViewDefinitionFromUserAttributes(zombieGroup.getUserAttributes());
export const pluckAndOrderFromViewDef = (orderedKeys, viewDefinition) => Object.fromEntries(orderedKeys.map((k) => [k, viewDefinition[k]]).filter(([k, v]) => v));
const listViewDefinitionMap = {
    Device: pluckAndOrderFromViewDef(['name', 'deviceType', 'eui', 'state'], getViewDefinitionFromUserAttributes(new Device({}).getUserAttributes())),
    Gateway: pluckAndOrderFromViewDef(['name', 'networkKey', 'eui', 'state'], getViewDefinitionFromUserAttributes(new Gateway({}).getUserAttributes())),
    Group: basicGroupListViewDefinition,
};
export const getListViewDefinition = (objectTypeId) => {
    return listViewDefinitionMap[objectTypeId] ?? undefined;
};
/** sets default values on normally new objects */
export const setDefaultValues = (newObject, viewDefinition) => {
    debug && console.log(`setDefaultValues`, { newObject, viewDefinition });
    Object.entries(viewDefinition).forEach(([k, vkd]) => {
        if (vkd.defaultValue)
            newObject[k] = vkd.defaultValue;
    });
    return newObject;
};
